import { Close } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Typography,
  alpha,
} from '@mui/material';

export const getConsultantName = (consultant) =>
  consultant?.fullName ||
  [consultant?.firstname, consultant?.lastname].filter(Boolean).join(' ') ||
  'this consultant';

const getInitials = (consultant) =>
  [consultant?.firstname, consultant?.lastname]
    .filter(Boolean)
    .map((n) => n.trim()[0]?.toUpperCase())
    .join('');

// Shared layout for consultant confirmation dialogs (deactivate, delete, ...)
export default function ConsultantConfirmDialog({
  open,
  onClose,
  consultant,
  color,
  icon,
  title,
  description,
  consequences = [],
  notice = null,
  confirmLabel,
  onConfirm,
  saving = false,
  confirmDisabled = false,
}) {
  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ px: 3, pt: 2.5 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '12px',
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(color, 0.1),
            color,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0, pt: 0.25 }}>
          <Typography variant="subtitle1" fontWeight={700} lineHeight={1.3}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {description}
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={saving} aria-label="Close" sx={{ mt: -0.5 }}>
          <Close />
        </IconButton>
      </Stack>

      <DialogContent sx={{ pt: '20px !important' }}>
        {/* Who this applies to */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ p: 1.5, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}
        >
          <Avatar sx={{ width: 40, height: 40, bgcolor: alpha('#0c5bab', 0.1), color: '#0c5bab', fontWeight: 700, fontSize: 15 }}>
            {getInitials(consultant)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap>
              {getConsultantName(consultant)}
            </Typography>
            {consultant?.email && (
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {consultant.email}
              </Typography>
            )}
          </Box>
        </Stack>

        {consequences.length > 0 && (
          <Box
            component="ul"
            sx={{
              mt: 2,
              mb: 0,
              pl: 2.5,
              color: 'text.secondary',
              '& li': { fontSize: 14, mb: 0.5 },
            }}
          >
            {consequences.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </Box>
        )}

        {notice && <Box sx={{ mt: 2 }}>{notice}</Box>}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
        <Button onClick={onClose} disabled={saving} color="inherit" sx={{ borderRadius: '8px' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={onConfirm}
          disabled={saving || confirmDisabled}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            borderRadius: '8px',
            px: 2.5,
            bgcolor: color,
            '&:hover': { bgcolor: color, filter: 'brightness(0.92)' },
          }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
